import React, { useState } from 'react';
import { Paper, Box, Typography, Chip, Collapse } from '@material-ui/core';
import type { ActivitySlot } from '../../../../_models/schedule.model';
import { getStatusColors, getToken, useThemeVersion } from '../../../../theme/chartTheme';

interface ConflictAnalysisPanelProps {
  slots: ActivitySlot[];
}

interface Conflict {
  type: 'instructor_overlap' | 'room_overlap' | 'gap';
  severity: 'high' | 'medium' | 'low';
  message: string;
  slots: ActivitySlot[];
}

const ConflictAnalysisPanel: React.FC<ConflictAnalysisPanelProps> = ({ slots }) => {
  const [expanded, setExpanded] = useState(true);
  const [filterSeverity, setFilterSeverity] = useState<'all' | 'high' | 'medium' | 'low'>('all');
  const themeVersion = useThemeVersion();
  // Resolved hex (not var()) — severity colors feed a `${color}30` alpha-suffix trick.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const statusColors = React.useMemo(() => getStatusColors(), [themeVersion]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const mutedColor = React.useMemo(() => getToken('--text-muted'), [themeVersion]);

  const detectConflicts = (): Conflict[] => {
    const conflicts: Conflict[] = [];

    // Detect instructor overlaps
    const instructorSlots = new Map<number, ActivitySlot[]>();
    slots.forEach((slot) => {
      if (slot.instructorId) {
        const existing = instructorSlots.get(slot.instructorId) || [];
        existing.push(slot);
        instructorSlots.set(slot.instructorId, existing);
      }
    });

    instructorSlots.forEach((instructorSlotList, instructorId) => {
      for (let i = 0; i < instructorSlotList.length; i++) {
        for (let j = i + 1; j < instructorSlotList.length; j++) {
          const slot1 = instructorSlotList[i];
          const slot2 = instructorSlotList[j];

          if (slot1.date === slot2.date && slot1.startTime && slot2.startTime && slot1.endTime && slot2.endTime) {
            const start1 = new Date(`2000-01-01T${slot1.startTime}`);
            const end1 = new Date(`2000-01-01T${slot1.endTime}`);
            const start2 = new Date(`2000-01-01T${slot2.startTime}`);
            const end2 = new Date(`2000-01-01T${slot2.endTime}`);

            // Check for overlap
            if (start1 < end2 && start2 < end1) {
              conflicts.push({
                type: 'instructor_overlap',
                severity: 'high',
                message: `${slot1.instructorName} has overlapping sessions on ${slot1.date}`,
                slots: [slot1, slot2],
              });
            }
          }
        }
      }
    });

    // Detect room overlaps
    const roomSlots = new Map<number, ActivitySlot[]>();
    slots.forEach((slot) => {
      if (slot.roomId) {
        const existing = roomSlots.get(slot.roomId) || [];
        existing.push(slot);
        roomSlots.set(slot.roomId, existing);
      }
    });

    roomSlots.forEach((roomSlotList) => {
      for (let i = 0; i < roomSlotList.length; i++) {
        for (let j = i + 1; j < roomSlotList.length; j++) {
          const slot1 = roomSlotList[i];
          const slot2 = roomSlotList[j];

          if (slot1.date === slot2.date && slot1.startTime && slot2.startTime && slot1.endTime && slot2.endTime) {
            const start1 = new Date(`2000-01-01T${slot1.startTime}`);
            const end1 = new Date(`2000-01-01T${slot1.endTime}`);
            const start2 = new Date(`2000-01-01T${slot2.startTime}`);
            const end2 = new Date(`2000-01-01T${slot2.endTime}`);

            if (start1 < end2 && start2 < end1) {
              conflicts.push({
                type: 'room_overlap',
                severity: 'high',
                message: `Room "${slot1.roomName}" is double-booked on ${slot1.date}`,
                slots: [slot1, slot2],
              });
            }
          }
        }
      }
    });

    // Detect large gaps in instructor schedules (>3 hours between sessions on same day)
    instructorSlots.forEach((instructorSlotList, instructorId) => {
      const slotsByDate = new Map<string, ActivitySlot[]>();
      instructorSlotList.forEach((slot) => {
        if (slot.date) {
          const existing = slotsByDate.get(slot.date) || [];
          existing.push(slot);
          slotsByDate.set(slot.date, existing);
        }
      });

      slotsByDate.forEach((daySlots, date) => {
        const sorted = [...daySlots].sort((a, b) => {
          const timeA = a.startTime || '00:00';
          const timeB = b.startTime || '00:00';
          return timeA.localeCompare(timeB);
        });

        for (let i = 0; i < sorted.length - 1; i++) {
          const current = sorted[i];
          const next = sorted[i + 1];

          if (current.endTime && next.startTime) {
            const end = new Date(`2000-01-01T${current.endTime}`);
            const start = new Date(`2000-01-01T${next.startTime}`);
            const gapHours = (start.getTime() - end.getTime()) / (1000 * 60 * 60);

            if (gapHours > 3) {
              conflicts.push({
                type: 'gap',
                severity: gapHours > 5 ? 'medium' : 'low',
                message: `${current.instructorName} has a ${gapHours.toFixed(1)}h gap between sessions on ${date}`,
                slots: [current, next],
              });
            }
          }
        }
      });
    });

    return conflicts;
  };

  const conflicts = detectConflicts();
  const filteredConflicts =
    filterSeverity === 'all' ? conflicts : conflicts.filter((c) => c.severity === filterSeverity);

  const getSeverityColor = (severity: string): string => {
    switch (severity) {
      case 'high':
        return statusColors.danger;
      case 'medium':
        return statusColors.warning;
      case 'low':
        return statusColors.info;
      default:
        return mutedColor;
    }
  };

  const getSeverityIcon = (severity: string): string => {
    switch (severity) {
      case 'high':
        return '🚨';
      case 'medium':
        return '⚠️';
      case 'low':
        return 'ℹ️';
      default:
        return '📋';
    }
  };

  const getTypeLabel = (type: string): string => {
    switch (type) {
      case 'instructor_overlap':
        return 'Instructor Conflict';
      case 'room_overlap':
        return 'Room Conflict';
      case 'gap':
        return 'Schedule Gap';
      default:
        return 'Unknown';
    }
  };

  const counts = {
    high: conflicts.filter((c) => c.severity === 'high').length,
    medium: conflicts.filter((c) => c.severity === 'medium').length,
    low: conflicts.filter((c) => c.severity === 'low').length,
  };

  return (
    <Paper elevation={2} style={{ padding: '24px', borderRadius: '12px' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h6" style={{ fontWeight: 600, color: 'var(--text-strong)' }}>
            Conflict & Gap Analysis
          </Typography>
          <Typography variant="body2" style={{ color: 'var(--text-muted)', marginTop: 4 }}>
            {conflicts.length === 0 ? 'No conflicts detected' : `${conflicts.length} issues found`}
          </Typography>
        </Box>
        <button
          onClick={() => setExpanded(!expanded)}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: '20px',
            padding: '8px',
          }}
        >
          {expanded ? '🔼' : '🔽'}
        </button>
      </Box>

      <Collapse in={expanded}>
        <Box mb={3} display="flex" style={{ gap: '12px' }} flexWrap="wrap">
          <Chip
            label={`All (${conflicts.length})`}
            onClick={() => setFilterSeverity('all')}
            style={{
              background: filterSeverity === 'all' ? 'var(--brand-primary)' : 'var(--border-default)',
              color: filterSeverity === 'all' ? 'white' : 'var(--text-muted)',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          />
          <Chip
            label={`🚨 High (${counts.high})`}
            onClick={() => setFilterSeverity('high')}
            style={{
              background: filterSeverity === 'high' ? 'var(--status-danger)' : 'var(--border-default)',
              color: filterSeverity === 'high' ? 'white' : 'var(--text-muted)',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          />
          <Chip
            label={`⚠️ Medium (${counts.medium})`}
            onClick={() => setFilterSeverity('medium')}
            style={{
              background: filterSeverity === 'medium' ? 'var(--status-warning)' : 'var(--border-default)',
              color: filterSeverity === 'medium' ? 'white' : 'var(--text-muted)',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          />
          <Chip
            label={`ℹ️ Low (${counts.low})`}
            onClick={() => setFilterSeverity('low')}
            style={{
              background: filterSeverity === 'low' ? 'var(--status-info)' : 'var(--border-default)',
              color: filterSeverity === 'low' ? 'white' : 'var(--text-muted)',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          />
        </Box>

        {filteredConflicts.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Typography variant="h3" style={{ marginBottom: 8 }}>
              ✅
            </Typography>
            <Typography variant="body1" style={{ color: 'var(--text-muted)' }}>
              {conflicts.length === 0 ? 'No conflicts or issues detected' : 'No issues in this category'}
            </Typography>
          </Box>
        ) : (
          <Box style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {filteredConflicts.map((conflict, index) => (
              <Box
                key={index}
                mb={2}
                p={2}
                style={{
                  background: 'var(--surface-page)',
                  border: `2px solid ${getSeverityColor(conflict.severity)}30`,
                  borderLeft: `4px solid ${getSeverityColor(conflict.severity)}`,
                  borderRadius: '8px',
                }}
              >
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <Box display="flex" alignItems="center" style={{ gap: '8px' }}>
                    <span style={{ fontSize: '18px' }}>{getSeverityIcon(conflict.severity)}</span>
                    <Typography variant="body2" style={{ fontWeight: 600, color: 'var(--text-strong)' }}>
                      {getTypeLabel(conflict.type)}
                    </Typography>
                  </Box>
                  <Chip
                    label={conflict.severity.toUpperCase()}
                    size="small"
                    style={{
                      background: getSeverityColor(conflict.severity),
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '10px',
                    }}
                  />
                </Box>
                <Typography variant="body2" style={{ color: 'var(--text-muted)', marginBottom: 8 }}>
                  {conflict.message}
                </Typography>
                <Box display="flex" style={{ gap: '8px' }} flexWrap="wrap">
                  {conflict.slots.map((slot, idx) => (
                    <Typography
                      key={idx}
                      variant="caption"
                      style={{
                        background: 'white',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        border: '1px solid var(--border-default)',
                        color: 'var(--text-strong)',
                        fontSize: '11px',
                      }}
                    >
                      {slot.startTime} - {slot.endTime} | {slot.topicName || 'No topic'}
                    </Typography>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Collapse>
    </Paper>
  );
};

export default ConflictAnalysisPanel;
